// src/components/ProfilePage.jsx
//
// Shows the logged-in user's own profile and lets them edit it in place,
// right inside the same "User Details" card. Reads the logged-in user's
// id from localStorage("currentUser") -- see the note below about where
// that gets set. This is always a SELF profile page: there's no route
// param for viewing someone else's -- editing another user's record is
// Account > Users' Edit action instead (EditUserForm.jsx), gated to
// Admin/Super Admin there.
//
// Restyled to match the requested mockup: a "User Details" card (photo,
// Name/Mob No/Email ID/Role/Department/Company, and Account Status/
// Access Status shown as toggle switches) with an EDIT button at the
// bottom-right. Per a later request, clicking EDIT no longer opens a
// separate popup dialog -- the `isEditing` flag below just swaps each
// field in this same card between its read-only display and its
// editable input/select, and the EDIT button becomes Cancel/Save
// Changes underneath the same fields.
//
// GET /profile/<id> returns MobileNo/DepartmentID/DepartmentName/
// CompanyID/CompanyName/RoleID/IsEnabled/Status. PUT /profile/<id>
// accepts edits to Name/MobileNo/Department/Company, Role (Admin/Super
// Admin only), and Account Status/Status + Access Status/IsEnabled
// (both Super Admin only) -- see app.py.
//
// Role editing: View/Contributor always see Role as plain read-only
// text. Admin and Super Admin see it the same way until isEditing, at
// which point it becomes a <select> pre-filled with their current
// role -- Super Admin can pick any of the four tiers, Admin can pick
// any tier EXCEPT Super Admin (the backend enforces this too, so this
// isn't just a UI restriction -- see PUT /profile/<id> in app.py).
// This is deliberately self-only: there's no way to change anyone
// else's role from this page, only your own.
//
// Account Status / Access Status: rendered as toggle switches in both
// read-only and editing states, but only ever interactive for a Super
// Admin editing their own profile -- every other tier sees the same
// switches, just disabled, so they can see the state without being
// able to flip it. Per explicit request, Account Status (the Status
// column, Active/Inactive) is now editable the same way Access Status
// already was -- Super Admin only, both directions. It used to be a
// pure computed field (Active only once the invite link was used to
// set a password) and stayed disabled for everyone including Super
// Admin, but that meant there was no way to manually activate an
// account whose invite email never arrived; this trades that
// protection for that ability. Manually setting an already-active
// account back to Inactive will make the login page show its normal
// "check your email for the invite link" message even though nothing
// was actually sent -- Access Status (IsEnabled) is the better tool
// for simply blocking someone's login, with its own clear "disabled by
// administrator" message.
import React, { useEffect, useRef, useState } from "react";
import { API_ORIGIN, authFetch } from "../config/apiConfig";
import { getCurrentTier } from "../utils/permissions";

const API_BASE = API_ORIGIN;

// Small toggle-switch control shared by the read-only card and the
// edit modal below -- both need the exact same look, just wired
// differently (static there, live here).
function ToggleSwitch({ checked, disabled, onChange, label, required, onText = "Enable", offText = "Disabled" }) {
  // Per request, order is now: label, then the toggle itself, then a
  // plain-language readout of whatever the toggle is currently set to
  // -- previously this was just [toggle][label], with no text saying
  // which state a given position actually meant. `onText`/`offText`
  // default to "Enable"/"Disabled" (Access Status's wording) but Account
  // Status below passes "Active"/"Inactive" instead, since that's what
  // that field's two states actually mean.
  return (
    <div className="d-flex align-items-center gap-2">
      <span className={disabled ? "text-muted" : "fw-semibold text-dark"}>
        {label}
        {required && <span className="text-danger"> *</span>}
      </span>
      <label className={`profile-toggle${disabled ? " profile-toggle-disabled" : ""}`}>
        <input
          type="checkbox"
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => onChange && onChange(e.target.checked)}
        />
        <span className="profile-toggle-slider" />
      </label>
      <span className={`fw-semibold ${checked ? "text-success" : "text-danger"}`}>
        {checked ? onText : offText}
      </span>
    </div>
  );
}

export default function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [tiers, setTiers] = useState([]); // [{RoleID, RoleName}] from /permission-tiers

  // Per request, editing now happens in place inside the same
  // "User Details" card instead of opening a separate popup dialog --
  // this flag just swaps each field between its read-only display and
  // its editable input/select, and the EDIT button becomes Cancel/Save
  // Changes. Renamed from showEditModal/openEditModal/closeEditModal
  // (the old popup-based names) to isEditing/startEditing/cancelEditing.
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({ UserName: "", MobileNo: "", DepartmentID: "", CompanyID: "", RoleID: "", IsEnabled: false, IsActive: false });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  // FIX: was a showSavedModal flag that popped up a full-screen teal
  // overlay ("Profile updated successfully.") the user had to click OK
  // to dismiss. Per request, replaced with an inline success banner
  // (savedMsg) shown in the same card, right where saveError already
  // shows -- no popup, nothing to click through.
  const [savedMsg, setSavedMsg] = useState("");
  const [roleChanged, setRoleChanged] = useState(false);

  // IMPL-10 -- Two-Factor Authentication (Security card, below the
  // User Details card). `mfaEnroll` holds the in-progress enroll step
  // (secret + QR provisioning URI) between POST /mfa/totp/enroll and
  // the user submitting a code to POST /mfa/totp/confirm; it's cleared
  // again on confirm/cancel/disable. `totpEnabled` mirrors
  // profile.TotpEnabled but is kept separately so it can flip the
  // instant confirm/disable succeeds, without waiting on a full
  // loadProfile() round trip.
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [mfaEnroll, setMfaEnroll] = useState(null); // { secret, provisioning_uri }
  const [mfaCode, setMfaCode] = useState("");
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaMsg, setMfaMsg] = useState("");

  const storedUser = JSON.parse(localStorage.getItem("currentUser") || "null");
  const userId = storedUser?.id;
  const viewerTier = getCurrentTier();
  const canEditRole = viewerTier === "Admin" || viewerTier === "Super Admin";
  const canEditAccessStatus = viewerTier === "Super Admin";

  const loadProfile = () => {
    if (!userId) {
      setError("You're not logged in.");
      setLoading(false);
      return;
    }
    setLoading(true);
    authFetch(`${API_BASE}/profile/${userId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          setError(data.error);
        } else {
          setProfile(data);
          setTotpEnabled(!!data.TotpEnabled);
        }
      })
      .catch(() => setError("Could not load your profile."))
      .finally(() => setLoading(false));
  };

  // IMPL-10 -- Two-Factor Authentication handlers.
  const startMfaEnroll = async () => {
    setMfaBusy(true);
    setMfaMsg("");
    try {
      const res = await authFetch(`${API_BASE}/mfa/totp/enroll`, { method: "POST" });
      const data = await res.json();
      if (data.success) {
        setMfaEnroll({ secret: data.secret, provisioning_uri: data.provisioning_uri });
      } else {
        setMfaMsg(data.message || "Could not start enrollment.");
      }
    } catch {
      setMfaMsg("Could not start enrollment.");
    } finally {
      setMfaBusy(false);
    }
  };

  const confirmMfaEnroll = async (e) => {
    e.preventDefault();
    if (!mfaCode || mfaCode.trim().length < 6) {
      setMfaMsg("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setMfaBusy(true);
    setMfaMsg("");
    try {
      const res = await authFetch(`${API_BASE}/mfa/totp/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: mfaCode.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setTotpEnabled(true);
        setMfaEnroll(null);
        setMfaCode("");
        setMfaMsg("");
      } else {
        setMfaMsg(data.message || "Invalid code.");
      }
    } catch {
      setMfaMsg("Could not confirm enrollment.");
    } finally {
      setMfaBusy(false);
    }
  };

  const cancelMfaEnroll = () => {
    setMfaEnroll(null);
    setMfaCode("");
    setMfaMsg("");
  };

  const disableMfa = async () => {
    setMfaBusy(true);
    setMfaMsg("");
    try {
      const res = await authFetch(`${API_BASE}/mfa/totp/disable`, { method: "POST" });
      const data = await res.json();
      if (data.success) {
        setTotpEnabled(false);
      } else {
        setMfaMsg(data.message || "Could not disable two-factor authentication.");
      }
    } catch {
      setMfaMsg("Could not disable two-factor authentication.");
    } finally {
      setMfaBusy(false);
    }
  };

  useEffect(() => {
    loadProfile();
    // Department/Company dropdowns -- same endpoints EditUserForm.jsx
    // already uses. Role options only matter for Admin/Super Admin,
    // but fetching them is harmless (and cheap -- find-or-create,
    // idempotent) for everyone.
    authFetch(`${API_BASE}/departments`).then((res) => res.json()).then((data) => setDepartments(Array.isArray(data) ? data : [])).catch(() => setDepartments([]));
    authFetch(`${API_BASE}/companies`).then((res) => res.json()).then((data) => setCompanies(Array.isArray(data) ? data : [])).catch(() => setCompanies([]));
    authFetch(`${API_BASE}/permission-tiers`).then((res) => res.json()).then((data) => setTiers(Array.isArray(data) ? data : [])).catch(() => setTiers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    handleUpload(file);
  };

  const handleUpload = async (file) => {
    setError("");
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("photo", file);

      const res = await authFetch(`${API_BASE}/profile/${userId}/photo`, {
        method: "POST",
        body: formData
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to upload photo.");
        return;
      }

      setProfile((prev) => ({ ...prev, ProfileImage: data.ProfileImage }));

      // Keep the cached user in sync so Header.jsx picks up the new
      // photo without needing a full re-login.
      const updatedUser = { ...storedUser, ProfileImage: data.ProfileImage };
      localStorage.setItem("currentUser", JSON.stringify(updatedUser));
      // FIX: writing localStorage alone doesn't make Layout.jsx (which
      // stays mounted across route changes) re-read it -- the header
      // avatar kept showing the old photo/initials until a full page
      // reload. This event tells Layout.jsx to refresh right away.
      window.dispatchEvent(new Event("currentUserUpdated"));
    } catch (err) {
      setError("Network error while uploading the photo.");
    } finally {
      setUploading(false);
    }
  };

  const startEditing = () => {
    if (!profile) return;
    setSaveError("");
    setSavedMsg("");
    setForm({
      UserName: profile.UserName || "",
      MobileNo: profile.MobileNo || "",
      DepartmentID: profile.DepartmentID || "",
      CompanyID: profile.CompanyID || "",
      RoleID: profile.RoleID || "",
      IsEnabled: !!profile.IsEnabled,
      IsActive: profile.Status === "Active",
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    if (saving) return;
    setIsEditing(false);
    setSaveError("");
  };

  const handleFormChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveError("");

    if (!form.UserName.trim()) {
      setSaveError("Name is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        UserName: form.UserName.trim(),
        MobileNo: form.MobileNo.trim() || null,
        DepartmentID: form.DepartmentID || null,
        CompanyID: form.CompanyID || null,
      };
      // Only send RoleID at all when this viewer is allowed to change
      // it -- View/Contributor never include it, so a stray value in
      // `form` (there shouldn't be one) can't sneak a role change
      // through for them.
      if (canEditRole && form.RoleID) {
        payload.RoleID = form.RoleID;
      }
      // Same rule for Access Status and Account Status -- only a Super
      // Admin's request ever includes them; the backend re-checks this
      // too.
      if (canEditAccessStatus) {
        payload.IsEnabled = form.IsEnabled;
        payload.Status = form.IsActive ? "Active" : "Inactive";
      }

      const res = await authFetch(`${API_BASE}/profile/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (!res.ok) {
        setSaveError(data.error || "Failed to update your profile.");
        return;
      }

      setRoleChanged(canEditRole && !!form.RoleID && Number(form.RoleID) !== Number(profile.RoleID));
      setIsEditing(false);
      setSavedMsg("Profile updated successfully.");
      loadProfile();
    } catch (err) {
      setSaveError("Network error while saving your profile.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <p className="text-muted mb-0">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="alert alert-danger mb-0">{error}</div>
        </div>
      </div>
    );
  }

  const displayName = profile?.UserName || "-";
  const photoUrl =
    previewUrl ||
    (profile?.ProfileImage ? `${API_BASE}/profile_photo/${profile.ProfileImage}` : null);
  const initials = displayName && displayName !== "-" ? displayName[0].toUpperCase() : "?";

  // Admin can hand themselves any tier except Super Admin -- only a
  // Super Admin can create another Super Admin. Filtered here so it's
  // never even offered as an option; the backend rejects it too if
  // somehow bypassed.
  const roleOptions =
    viewerTier === "Super Admin" ? tiers : tiers.filter((t) => t.RoleName !== "Super Admin");

  // Shared photo block -- identical markup used in both the read-only
  // card and the edit modal.
  const photoBlock = (
    <div className="col-12 mb-4 text-center">
      <label className="form-label fw-medium text-green d-block mb-3">Profile Photo</label>
      <div className="d-inline-block position-relative">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={displayName}
            className="object-fit-cover rounded-circle shadow-sm"
            height="110"
            width="110"
            style={{ border: "4px solid #0e4d52" }}
          />
        ) : (
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle shadow-sm"
            style={{
              height: 110,
              width: 110,
              border: "4px solid #0e4d52",
              background: "#123840",
              color: "#fff",
              fontSize: "2.5rem",
              fontWeight: 700
            }}
          >
            {initials}
          </span>
        )}
        <button
          type="button"
          className="btn btn-light border btn-sm shadow-sm d-flex align-items-center justify-content-center position-absolute bottom-0 end-0 rounded-circle p-1"
          title="Change photo"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          style={{ width: 32, height: 32, transform: "translate(10%, -10%)", zIndex: 5 }}
        >
          <i className="ti ti-edit fs-6 text-dark" />
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        className="d-none"
        accept="image/png, image/jpeg, image/gif, image/webp"
        onChange={handleFileChange}
      />
      {uploading && <div className="text-muted fs-13 mt-2">Uploading...</div>}
    </div>
  );

  return (
    <>
      <div className="d-flex align-items-center page-title-head">
        <div className="flex-grow-1">
          <h4 className="m-0 page-main-title">Profile</h4>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-8 col-lg-7">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom px-4 py-3">
              <h5 className="fw-bold text-green fs-5 m-0">User Details</h5>
            </div>

            {/* Per request, Edit now happens in the same card/window
                instead of a popup dialog -- each field below swaps
                between its read-only display and its editable
                input/select depending on `isEditing`, and the EDIT
                button becomes Cancel/Save Changes. The whole card body
                is one <form> now (harmless while read-only, since
                nothing is required/submittable until isEditing). */}
            <form onSubmit={handleSave}>
              <div className="card-body p-4">
                {error && <div className="alert alert-danger">{error}</div>}
                {isEditing && saveError && <div className="alert alert-danger">{saveError}</div>}

                {photoBlock}

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">
                      Name{isEditing && <span className="text-danger"> *</span>}
                    </label>
                    {isEditing ? (
                      <input
                        className="form-control"
                        value={form.UserName}
                        onChange={handleFormChange("UserName")}
                        required
                      />
                    ) : (
                      <input className="form-control custom-field py-2" value={displayName} readOnly />
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">Mob No</label>
                    {isEditing ? (
                      <input
                        type="tel"
                        className="form-control"
                        value={form.MobileNo}
                        onChange={handleFormChange("MobileNo")}
                      />
                    ) : (
                      <input className="form-control custom-field py-2" value={profile?.MobileNo || "-"} readOnly />
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">Email ID</label>
                    {/* Never editable -- it's the login identifier; a
                        real email change goes through Account > Users
                        > Edit (an Admin/Super Admin action), not
                        self-service. */}
                    <input className="form-control custom-field py-2" value={profile?.Email || "-"} type="email" readOnly />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">Role</label>
                    {isEditing && canEditRole ? (
                      <>
                        <select className="form-select" value={form.RoleID} onChange={handleFormChange("RoleID")}>
                          {roleOptions.map((t) => (
                            <option key={t.RoleID} value={t.RoleID}>{t.RoleName}</option>
                          ))}
                        </select>
                        {viewerTier === "Admin" && (
                          <div className="form-text">Admins cannot assign themselves the Super Admin tier.</div>
                        )}
                      </>
                    ) : (
                      <input className="form-control custom-field py-2" value={profile?.RoleName || "-"} readOnly />
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">Department</label>
                    {isEditing ? (
                      <select className="form-select" value={form.DepartmentID} onChange={handleFormChange("DepartmentID")}>
                        <option value="">-- Select Department --</option>
                        {departments.map((d) => (
                          <option key={d.DepartmentID} value={d.DepartmentID}>{d.DepartmentName}</option>
                        ))}
                      </select>
                    ) : (
                      <input className="form-control custom-field py-2" value={profile?.DepartmentName || "-"} readOnly />
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">Company</label>
                    {isEditing ? (
                      <select className="form-select" value={form.CompanyID} onChange={handleFormChange("CompanyID")}>
                        <option value="">-- Select Company --</option>
                        {companies.map((c) => (
                          <option key={c.CompanyID} value={c.CompanyID}>{c.CompanyName}</option>
                        ))}
                      </select>
                    ) : (
                      <input className="form-control custom-field py-2" value={profile?.CompanyName || "-"} readOnly />
                    )}
                  </div>

                  <div className="col-md-6 d-flex align-items-center">
                    {/* Per explicit request, now editable the same way
                        Access Status is -- Super Admin only, both
                        directions (see the note at the top of this
                        file for the tradeoff that comes with that). */}
                    {isEditing ? (
                      <ToggleSwitch
                        checked={form.IsActive}
                        disabled={!canEditAccessStatus}
                        onChange={(val) => setForm((prev) => ({ ...prev, IsActive: val }))}
                        label="Acount Status"
                        onText="Active"
                        offText="Inactive"
                      />
                    ) : (
                      <ToggleSwitch checked={profile?.Status === "Active"} disabled label="Acount Status" onText="Active" offText="Inactive" />
                    )}
                  </div>

                  <div className="col-md-6 d-flex align-items-center">
                    {isEditing ? (
                      <ToggleSwitch
                        checked={form.IsEnabled}
                        disabled={!canEditAccessStatus}
                        onChange={(val) => setForm((prev) => ({ ...prev, IsEnabled: val }))}
                        label="Access Status"
                      />
                    ) : (
                      <ToggleSwitch checked={!!profile?.IsEnabled} disabled label="Access Status" />
                    )}
                  </div>
                </div>

                {/* Per request, moved down here from the top of the card
                    (right under the header) so it shows near the
                    EDIT/Save Changes button instead of above the photo. */}
                {!isEditing && savedMsg && (
                  <div className="alert alert-success d-flex align-items-center mt-4 mb-0">
                    <i className="ti ti-circle-check me-2 fs-5" />
                    <span>
                      {savedMsg}
                      {roleChanged && " Your new role takes effect the next time you log in."}
                    </span>
                  </div>
                )}

                <div className="text-end mt-4">
                  {isEditing ? (
                    <div className="d-flex justify-content-end gap-2">
                      <button type="button" className="btn btn-light border" onClick={cancelEditing} disabled={saving}>
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary px-4" disabled={saving}>
                        {saving ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  ) : (
                    <button type="button" className="btn btn-primary px-4" onClick={startEditing}>
                      EDIT
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* IMPL-10 -- opt-in authenticator-app (TOTP) two-factor
          authentication. Deliberately its own card, separate from User
          Details above -- this isn't a profile field, it's a security
          setting. No QR image is rendered here (that would mean
          sending the raw MFA secret to a third-party QR-image service)
          -- the secret is shown as plain text for "enter a setup key
          manually" instead, which every authenticator app supports. */}
      <div className="row mt-4">
        <div className="col-12 col-md-8 col-lg-7">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom px-4 py-3">
              <h5 className="fw-bold text-green fs-5 m-0">Two-Factor Authentication</h5>
            </div>
            <div className="card-body p-4">
              {mfaMsg && (
                <div className="alert alert-warning py-2" role="alert">{mfaMsg}</div>
              )}

              {totpEnabled ? (
                <>
                  <p className="mb-3">
                    <span className="badge bg-success-subtle text-success me-2">Enabled</span>
                    Your account requires an authenticator app code at login, in addition to the emailed OTP.
                  </p>
                  <button type="button" className="btn btn-outline-danger" onClick={disableMfa} disabled={mfaBusy}>
                    {mfaBusy ? "Disabling..." : "Disable Two-Factor Authentication"}
                  </button>
                </>
              ) : mfaEnroll ? (
                <form onSubmit={confirmMfaEnroll}>
                  <p className="mb-2">
                    In your authenticator app (Google Authenticator, Microsoft Authenticator, Authy, etc.), choose
                    "Enter a setup key manually" and enter:
                  </p>
                  <div className="mb-3">
                    <code className="user-select-all fs-6">{mfaEnroll.secret}</code>
                  </div>
                  <p className="text-muted small mb-3">Account name: {storedUser?.email || "your login email"} · Issuer: Cost Estimation Tool</p>
                  <div className="mb-3" style={{ maxWidth: 220 }}>
                    <label className="form-label">Enter the 6-digit code it shows</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      className="form-control text-center"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/[^0-9]/g, ""))}
                      autoFocus
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-light border" onClick={cancelMfaEnroll} disabled={mfaBusy}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={mfaBusy}>
                      {mfaBusy ? "Confirming..." : "Confirm & Enable"}
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <p className="mb-3">
                    <span className="badge bg-secondary-subtle text-secondary me-2">Not enabled</span>
                    Add an authenticator app as a second login factor, alongside your existing email OTP.
                  </p>
                  <button type="button" className="btn btn-primary" onClick={startMfaEnroll} disabled={mfaBusy}>
                    {mfaBusy ? "Starting..." : "Enable Two-Factor Authentication"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Matches profile.html's .custom-field styling for the theme's
          readonly-look inputs and the Account/Access-status toggle
          switches -- all scoped to this page. (The popup dialog overlay
          rules that used to live here were removed along with the
          popup itself, per the in-place-editing request above.) */}
      <style>{`
        .custom-field {
          background-color: #f3f3f3 !important;
          border: 1px solid #dcdcdc !important;
          color: #333333 !important;
          border-radius: 2px;
        }

        .profile-toggle {
          position: relative;
          display: inline-block;
          width: 38px;
          height: 20px;
          margin: 0;
        }
        .profile-toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .profile-toggle-slider {
          position: absolute;
          inset: 0;
          background-color: #ccc;
          transition: 0.2s;
          border-radius: 20px;
          cursor: pointer;
        }
        .profile-toggle-slider:before {
          content: "";
          position: absolute;
          height: 14px;
          width: 14px;
          left: 3px;
          bottom: 3px;
          background-color: #fff;
          transition: 0.2s;
          border-radius: 50%;
        }
        .profile-toggle input:checked + .profile-toggle-slider {
          background-color: #124d52;
        }
        .profile-toggle input:checked + .profile-toggle-slider:before {
          transform: translateX(18px);
        }
        .profile-toggle-disabled .profile-toggle-slider {
          cursor: not-allowed;
          opacity: 0.55;
        }
      `}</style>
    </>
  );
}
